import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { AuthenticationService } from '@app/authentication/services/authentication.service';
import { TokenRefreshService } from '@app/authentication/services/token-refresh.service';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    // Use Injector for lazy resolution to avoid circular dependency:
    // ErrorInterceptor → TokenRefreshService → AuthenticationService → HttpClient → interceptors
    constructor(private injector: Injector) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((err) => {
                if (err.status === 401) {
                    // Don't retry refresh-token requests — avoid infinite loop
                    if (request.url.toLowerCase().includes('refresh-token')) {
                        this.getAuthService().logout();
                        return throwError(() => err);
                    }

                    // Check if another tab already refreshed the token
                    const stored = JSON.parse(localStorage.getItem('token') || 'null');
                    const currentToken = this.getAuthService().userValue?.token;
                    if (stored?.token && stored.token !== currentToken) {
                        // Another tab refreshed — use the new token and retry
                        this.getAuthService().userSubject.next(stored);
                        const retryReq = request.clone({
                            setHeaders: { Authorization: `Bearer ${stored.token}` },
                        });
                        return next.handle(retryReq);
                    }

                    // Try one token refresh, then retry the original request
                    return this.getTokenRefreshService().refresh().pipe(
                        switchMap(success => {
                            if (success) {
                                const newToken = this.getAuthService().userValue?.token;
                                const retryReq = request.clone({
                                    setHeaders: { Authorization: `Bearer ${newToken}` },
                                });
                                return next.handle(retryReq);
                            }
                            this.getAuthService().logout();
                            return throwError(() => err);
                        })
                    );
                }

                // 403 = permission denied, not an expired token — let the component handle it
                // (Don't force-logout; the user is still authenticated, just unauthorized for this action)

                if (err.status === 500 && request.url.toLowerCase().includes('refresh-token')) {
                    this.getAuthService().logout();
                }

                return throwError(() => err);
            }),
        );
    }

    private getAuthService(): AuthenticationService {
        return this.injector.get(AuthenticationService);
    }

    private getTokenRefreshService(): TokenRefreshService {
        return this.injector.get(TokenRefreshService);
    }
}
